<script setup lang="ts">
import { ref, computed } from 'vue';

interface PlaceholderTab {
  id: number;
  name: string;
  slug: string;
  placeholderType: 'favoritos' | 'recentemente-jogados' | null;
}

interface Props {
  headerTitle?: string; // Opcional agora
  tabs: PlaceholderTab[];
}

const props = defineProps<Props>();

const activeTab = ref(0);

// Ref para o componente filho (FavoriteGamesList ou LastGamesPlayed)
const currentContentRef = ref<any>(null);

// Verificação de autenticação
const { isAuthenticated } = storeToRefs(useAuthStore());

function selectTab(index: number) {
  activeTab.value = index;
}

const currentTab = computed(() => props.tabs[activeTab.value]);

// Computed para obter os controles do swiper do componente filho
const swiperControls = computed(() => {
  // Acessa o swiperControls do componente filho
  return currentContentRef.value?.swiperControls || null;
});

// Só mostra se estiver autenticado
const shouldShow = computed(() => isAuthenticated.value);
</script>

<template>
  <div
    v-if="shouldShow"
    class="tabsPlaceholder"
  >
    <!-- Header com Tabs + Botões de Navegação -->
    <div class="tabsPlaceholder__header">
      <!-- Tabs Navigation -->
      <div class="tabsPlaceholder__tabs-navigation">
        <button
          v-for="(tab, index) in tabs"
          :key="tab.id"
          class="tabsPlaceholder__tab-button"
          :class="{ active: activeTab === index }"
          @click="selectTab(index)"
        >
          <p class="text-[12px]">{{ tab.name }}</p>
        </button>
      </div>

      <!-- Botões de navegação do swiper -->
      <div
        v-if="swiperControls"
        class="tabsPlaceholder__navigation-buttons"
      >
        <div class="nav-button-wrapper">
          <button
            aria-label="Anterior"
            class="nav-button"
            :disabled="swiperControls.currentScrollLeft.value === 0"
            @click="swiperControls.scrollPrev"
          >
            <NuxtIcon
              class="text-[16px]"
              name="perfil-oficial/back"
            />
          </button>
        </div>

        <div class="nav-button-wrapper">
          <button
            aria-label="Próximo"
            class="nav-button"
            :disabled="
              swiperControls.currentScrollLeft.value +
                swiperControls.currentClientWidth.value >=
              swiperControls.currentScrollWidth.value - 1
            "
            @click="swiperControls.scrollNext"
          >
            <NuxtIcon
              class="text-[16px]"
              name="perfil-oficial/next"
            />
          </button>
        </div>
      </div>
    </div>

    <!-- Tab Content -->
    <div class="tabsPlaceholder__content">
      <!-- Jogos Favoritos -->
      <CasinoFavoriteGamesList
        v-if="currentTab?.placeholderType === 'favoritos'"
        ref="currentContentRef"
        :always-show="true"
        :hide-header="true"
      />

      <!-- Jogados Recentemente -->
      <CasinoLastGamesPlayed
        v-else-if="currentTab?.placeholderType === 'recentemente-jogados'"
        ref="currentContentRef"
        :always-show="true"
        :hide-header="true"
      />
    </div>
  </div>
</template>

<style scoped>
.tabsPlaceholder {
  border-radius: 8px;
  margin-bottom: 16px;
}

.tabsPlaceholder__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.tabsPlaceholder__tabs-navigation {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  flex: 1;
}

.tabsPlaceholder__tabs-navigation::-webkit-scrollbar {
  display: none;
}

.tabsPlaceholder__tab-button {
  padding: 8px 12px;
  font-weight: 400;
  height: 32px;
  border-radius: 12px;
  background: var(--colors-flat-default-flat, rgba(63, 63, 70, 0.4));
  vertical-align: middle;
  color: #ffffff;
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  transition: all 0.2s;
}

.tabsPlaceholder__tab-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.tabsPlaceholder__tab-button.active {
  height: 32px;
  border-radius: 12px;
  font-weight: 400;
  background: #1414dd;
  opacity: 1;
  vertical-align: middle;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 6px 8px -2px rgba(0, 112, 243, 0.4),
    0 4px 4px -1px rgba(0, 0, 0, 0.05);
}

.tabsPlaceholder__navigation-buttons {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.nav-button-wrapper {
  border-radius: 12px;
  background: rgba(63, 63, 70, 0.4);
}

.nav-button {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #ffffff;
  width: 32px;
  height: 32px;
  transition: opacity 0.2s;
}

.nav-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tabsPlaceholder__content {
  /* Conteúdo dos placeholders */
}

@media (min-width: 768px) {
  .tabsPlaceholder {
    margin-bottom: 20px;
  }

  .tabsPlaceholder__header {
    margin-bottom: 8px;
  }

  .tabsPlaceholder__tab-button {
    height: 44px;
    padding: 12px 24px;
    border-radius: 12px;
  }

  .tabsPlaceholder__tab-button.active {
    height: 44px;
    border-radius: 12px;
  }

  .nav-button-wrapper {
    border-radius: 12px;
  }

  .nav-button {
    width: 44px;
    height: 44px;
  }
}
</style>
